import React, { useState } from "react";
import { BCMSRecord, BCMSCurrency, BCMSValidate, ModalOpen, useParams, useRoute, useBCMSContext, useWorkflowStep, useBCMSModal } from "@bcm-dev/bcms-vite";
import { Card } from "model";
import FormContainer, { FormButtonContainer, FormRow } from "components/shared/containers/FormContainer";
import { UICancelButton, UIPrimaryButton } from "components/ui/UIButton";
import UITextInput from "components/ui/UITextInput";
import { getWorkflowSteps, getDefault } from "components/workflows/BalanceTransferWorkflow";

import "./styles.css";

/**
 * @function TransferAmountView
 * @return {React.Component}
 * @exported true
 * @description The main component for TransferAmountView
 */
export default function TransferAmountView(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor([ Card ], globalController);
  const workflowStep = useWorkflowStep(getDefault, getWorkflowSteps);
  const cardID = useParams().cardID ?? "0";
  const card = Card.initByID(cardID);
  const creditLimit = card.limit;
  const creditLimitFormatted = creditLimit.format();
  const availableCredit = BCMSCurrency.init(creditLimit.amount - card.balance.amount).format();
  const state = workflowStep.getStepState;
  const [ value, setValue ] = useState({...state});
  const transferAmount = value.transferAmount instanceof BCMSCurrency ? value.transferAmount.format() : value.transferAmount;
  const inputValuesAreFilled = value.transferAmount !== undefined && value.creditCardNum.length > 0;

  const inputChange = prop => val => {
    setValue({...value, [prop]: val});
  }

  const validateAmount = val => {
    if(BCMSValidate.isCurrency(val)) {
      return BCMSCurrency.init(val);
    } else {
      return BCMSCurrency.init(10000);
    }
  }

  const validateCreditCardNumber = val => {
    const defaultNum = "01234567890123456";
    if(!BCMSValidate.isNumber(val)) { return defaultNum; }
  
    if(val.length < 16) {
      return defaultNum;
    } else {
      return val;
    }
  }

  const next = evt => {
    workflowStep.update([ "transferAmount" ], validateAmount(value.transferAmount));
    workflowStep.update([ "creditCardNum" ], validateCreditCardNumber(value.creditCardNum));
    workflowStep.workflow.nextStep();
  }

  const navigate = useRoute();
  const { close } = useBCMSModal();
  const yesClicked = () => {
    close("CancelBalanceTransferModal");
    navigate(-3);
  }

  return (
    <div className="transfer-amount-view">
      <TransferAmountDetails availableCredit={ availableCredit }
       currentBalance={ card.balance.format() }
        creditLimit={ creditLimitFormatted } />
      <FormContainer>
        <FormRow>
          <p>Specify the amount and credit card number from which you wish to transfer the balance.</p>
        </FormRow>
        <FormRow>
          <UITextInput value={ transferAmount } placeholder="Transfer amount*" onChange={ inputChange("transferAmount") } />
        </FormRow>
        <FormRow>
          <UITextInput value={ value.creditCardNum } placeholder="Credit card*" type="password" onChange={ inputChange("creditCardNum") } />
        </FormRow>
        <FormButtonContainer>
          <UIPrimaryButton text="Next" className={ inputValuesAreFilled ? "" : "disabled" } onClick={ next } />
          {/*<UICancelButton text="Cancel" />*/}
          <ModalOpen id="CancelBalanceTransferModal" data= { {yesClicked: yesClicked} }>
            <UICancelButton text="Cancel" />
          </ModalOpen>

        </FormButtonContainer>
      </FormContainer>
    </div>
  );
}

/**
 * @function TransferAmountDetails
 * @return {React.Component}
 * @exported true
 * @description The main component for TransferAmountDetails
 */
export function TransferAmountDetails(props) {
  const availableCredit = props.availableCredit ?? "[$X.XX]";
  const currentBalance = props.currentBalance ?? "[$X.XX]";
  const creditLimit = props.creditLimit ?? "[$X.XX]";

  return (
    <div className="transfer-amount-details">
      <div className="flex-container flex-horizontal align-center-container content-container available-credit-container">
        <div className="flex-width text-left">
          <span className="available-credit-label label">Available Credit</span>
        </div>
        <div className="flex-width text-right">
          <span className="available-credit-text text"><b>{availableCredit}</b></span>
        </div>
      </div>
      <div className="flex-container flex-horizontal align-center-container content-container current-balance-container">
        <div className="flex-width text-left">
          <span className="credit-balance-label label">Current Balance</span>
        </div>
        <div className="flex-width text-right">
          <span className="credit-balance-text text"><b>{currentBalance}</b></span>
        </div>
      </div>
      <div className="flex-container flex-horizontal align-center-container content-container current-balance-container">
        <div className="flex-width text-left">
          <span className="credit-limit-label label">Credit Limit</span>
        </div>
        <div className="flex-width text-right">
          <span className="credit-limit-text text"><b>{ creditLimit }</b></span>
        </div>
      </div>
      <div className="flex-container flex-horizontal align-center-container content-container current-balance-container">
        <div className="flex-width text-left">
          <span className="apr-label label">APR</span>
        </div>
        <div className="flex-width text-right">
          <span className="apr-text text"><b>X.XX%</b></span>
        </div>
      </div>
    </div>
  );
}
